/**
 * Hooks for normalized tracker API endpoints.
 *
 * These hooks provide access to the normalized tracker system,
 * which maps tracker types (keywords, persons, etc.) to source-specific
 * endpoints and parameters.
 */

import { useApiQuery } from "@/hooks/use-api";
import type {
  SourceTrackerTypes,
  TrackerTypeResponse,
} from "@/models/normalized-tracker";

/**
 * Fetch all available tracker types.
 *
 * @returns Query result with list of tracker type names
 *
 * @example
 * const { data: types } = useNormalizedTrackerTypes();
 * // types = ["keywords", "persons", "groups", ...]
 */
export const useNormalizedTrackerTypes = (enabled: boolean = true) =>
  useApiQuery<string[]>(
    {
      url: "normalized/types",
      method: "GET",
    },
    { enabled }
  );

/**
 * Fetch all sources with their supported tracker types.
 *
 * @returns Query result with list of sources and their types
 *
 * @example
 * const { data: sources } = useNormalizedSources();
 * // sources = [
 * //   { source: "twitter", types: ["keywords", "persons", "groups"] },
 * //   { source: "tiktok", types: ["keywords", "hashtags", "persons"] },
 * //   ...
 * // ]
 */
export const useNormalizedSources = (enabled: boolean = true) =>
  useApiQuery<SourceTrackerTypes[]>(
    {
      url: "normalized/sources",
      method: "GET",
    },
    { enabled }
  );

/**
 * Fetch detailed tracker info for a specific type.
 *
 * This returns the endpoints and parameters needed to create
 * trackers of the specified type for each source.
 *
 * @param trackerType - The tracker type (e.g., "keywords", "persons")
 * @param sources - Optional list of sources to filter by
 * @param enabled - Whether to enable the query
 * @param searchQuery - Search value to get configured_params with correct param names
 *
 * @returns Query result with TrackerTypeResponse
 *
 * @example
 * const { data } = useNormalizedTrackersByType("keywords", ["tiktok"], true, "AI");
 * // data.sources = [{ source: "tiktok", search_term: "AI", info: { configured_params: { keyword: "AI" }, ... } }]
 */
export const useNormalizedTrackersByType = (
  trackerType: string | null,
  sources?: string[],
  enabled: boolean = true,
  searchQuery?: string
) => {
  // Build query params
  const params = new URLSearchParams();
  if (sources?.length) {
    params.set("sources", sources.join(","));
  }
  if (searchQuery) {
    params.set("query", searchQuery);
  }
  const queryString = params.toString() ? `?${params.toString()}` : "";

  return useApiQuery<TrackerTypeResponse>(
    {
      url: `normalized/trackers/${trackerType}${queryString}`,
      method: "GET",
    },
    {
      enabled: enabled && !!trackerType,
    }
  );
};

/**
 * Get sources that support a specific tracker type.
 *
 * This is a derived hook that filters sources by type.
 *
 * @param trackerType - The tracker type to filter by
 * @param enabled - Whether to enable the query
 *
 * @returns Query result with filtered source names
 */
export const useSourcesForType = (
  trackerType: string | null,
  enabled: boolean = true
) => {
  const { data: allSources, ...rest } = useNormalizedSources(enabled);

  const filteredSources = allSources?.filter((s) =>
    s.types.includes(trackerType || "")
  );

  return {
    ...rest,
    data: filteredSources?.map((s) => s.source),
  };
};
